import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { TableName } from 'modules/core/enums';
import { IField } from 'modules/core/types';
import {
  FEES_AND_TAXES_FIELDS_MAP_BY_TABLE,
  LENDERS_FEES_AND_TAXES_FIELDS,
} from 'modules/core/shared/feesAndTaxes.config';
import { LENDER_PREFERENCES_FIELDS, PE_PREFERENCES_FIELDS } from 'modules/core/shared/preferencesConfig.const';
import { APPROVED_LOAN_TERMS_FIELD_NAMES } from 'modules/RACAffordabilityEngine/const/fieldNames.const';
import { getFeaturesList } from 'store/selectors/ui.selector';

import { useVendorFieldAndOptions } from '../../../../hooks/useVendorFieldAndOptions';
import { useLenderPreferencesFields } from './useLenderPreferencesFields';
import { FRONT_AND_BACK_ADDS_FIELDS } from '../settingsModal.const';

interface IUseSettingsModalFieldsResult {
  preferencesFields: IField[];
  taxesFeesFields: IField[];
  lenderFeesFields: IField[];
}

export const useSettingsModalFields = (tableName: TableName): IUseSettingsModalFieldsResult => {
  const { is_calculated_book_value } = useSelector(getFeaturesList);
  const { VENDOR_FIELD } = useVendorFieldAndOptions();
  const preferencesForLender = useLenderPreferencesFields();

  const preferencesFields = useMemo((): IField[] => {
    if (tableName === TableName.PE) {
      return [
        ...PE_PREFERENCES_FIELDS.slice(0, 1),
        ...(is_calculated_book_value ? [] : [VENDOR_FIELD]),
        ...PE_PREFERENCES_FIELDS.slice(2),
      ];
    }

    if (tableName === TableName.LENDER) {
      return preferencesForLender;
    }

    return [...PE_PREFERENCES_FIELDS.slice(0, 1), ...LENDER_PREFERENCES_FIELDS];
  }, [tableName, is_calculated_book_value, VENDOR_FIELD, preferencesForLender]);

  const lenderFeesWithoutAdds = useMemo(
    () =>
      LENDERS_FEES_AND_TAXES_FIELDS.filter((f) => f.name !== APPROVED_LOAN_TERMS_FIELD_NAMES.FRONT_AND_BACK_ENDS_ADDS),
    [],
  );

  const lenderFeesFields = useMemo(() => {
    const bankFeesIndex = lenderFeesWithoutAdds.findIndex((f) => f.name === 'feesAndTaxes.bankFees');
    const insertIndex = bankFeesIndex >= 0 ? bankFeesIndex : lenderFeesWithoutAdds.length;

    return [
      ...lenderFeesWithoutAdds.slice(0, insertIndex),
      ...FRONT_AND_BACK_ADDS_FIELDS,
      ...lenderFeesWithoutAdds.slice(insertIndex),
    ];
  }, [lenderFeesWithoutAdds]);

  const taxesFeesFields = useMemo(
    () => (tableName === TableName.LENDER ? lenderFeesFields : FEES_AND_TAXES_FIELDS_MAP_BY_TABLE.get(tableName) || []),
    [tableName, lenderFeesFields],
  );

  return {
    preferencesFields,
    taxesFeesFields,
    lenderFeesFields: lenderFeesWithoutAdds,
  };
};
